/** Stateful command dispatcher with POSIX short-circuit joins and byte-preserving pipes. */

import { NotImplementedError } from '@riftydev/io';
import {
  type FsSync,
  type VfsMutationGuard,
  guardVfsMutations,
  isAbsolute,
  joinPath,
  normalizePath,
  syncMirror,
} from '@riftydev/vfs';
import { builtinCommands } from './builtins.ts';
import { CommandResolver } from './command-resolver.ts';
import { suggestCommandName } from './command-suggestion.ts';
import { hasGlobMeta, matchSegment } from './commands/_glob.ts';
import { resolve } from './commands/_shared.ts';
import type { ShellJobListItem } from './commands/jobs.ts';
import { npx } from './commands/npx.ts';
import { type ShellCompletionResult, createShellCompleter } from './language-service.ts';
import { type Token, isOp, tokenize } from './tokenize.ts';
import type {
  CommandContext,
  ProcessExit,
  ShellCommand,
  ShellCommandResult,
  StdinReader,
  TerminalResizeSource,
} from './types.ts';

/**
 * Runs a resolved VFS Node entry and resolves its exit (ADR-0137/0362).
 * Receives the normalized absolute entry path, post-glob argv, and command
 * context (stdout/stderr/cwd/env/signal). The public name is retained for
 * compatibility with existing hosts.
 */
export type BinExecutor = (
  entryPath: string,
  args: string[],
  ctx: CommandContext,
) => Promise<ShellCommandResult>;

export interface ShellOptions {
  cwd?: string;
  env?: Record<string, string>;
  /** Instance-local file namespace; omitted calls resolve the ambient sync mirror. */
  fileSystem?: FsSync;
  /**
   * Injected by the host to run a resolved VFS Node entry (ADR-0137/0362).
   * Execution needs a Worker realm the shell layer can't reach, so the
   * playground wires it; absent ⇒ a found entry reports exit 126 rather than
   * the 127 of a genuine miss.
   */
  execBin?: BinExecutor;
  /** Host policy boundary for every authoritative VFS mutation. */
  mutationGuard?: VfsMutationGuard;
  /** Synchronous namespace policy; never acquires the mutation FIFO. */
  assertPortablePaths?: (absolutePaths: readonly string[]) => void;
  /** Host policy at actual dispatch, after expansion and short-circuit selection. */
  assertCommand?: (name: string, args: readonly string[]) => void;
  /** False rejects every background operator before any command starts. */
  allowBackground?: boolean;
}

export interface RunResult {
  exitCode: number;
  /** Exact final executed command exit (ADR-0257). */
  exit: ProcessExit;
  stdout: string;
  stderr: string;
}

/** Host process-control failure: terminal ownership is lost, so no exit can be invented. */
export class ShellCommandLifecycleError extends Error {
  override readonly name = 'ShellCommandLifecycleError';
}

function isShellCommandLifecycleFailure(error: unknown): boolean {
  if (error instanceof ShellCommandLifecycleError) return true;
  return (
    error instanceof AggregateError &&
    (error.errors as readonly unknown[]).some((entry) => isShellCommandLifecycleFailure(entry))
  );
}

/** Stream identifier for `RunOptions.onChunk`. */
export type ChunkStream = 'stdout' | 'stderr';

/**
 * Per-call options for {@link Shell.run}.
 *
 * `onChunk` fires synchronously before capture for stderr and terminal-visible
 * stdout. Redirected stdout and non-final pipe stdout are captured/routed but
 * not displayed. Keep it fast. Optional and additive — omitting it preserves
 * the blob-at-the-end contract.
 */
export interface RunOptions {
  readonly onChunk?: (chunk: string, stream: ChunkStream) => void;
  /**
   * Host-supplied cancellation (Ctrl+C / SIGINT). When it fires, `run` aborts
   * its internal per-run controller and resolves with exit `130` even if a
   * handler never returns on its own (a `vite`/`node http` dev server) — the
   * foreground complement to background `&`. Cooperative, not a hard kill: the
   * handler observes `ctx.signal` and winds down (ADR-0089).
   */
  readonly signal?: AbortSignal;
  /**
   * Keep an aborted run owned until its command handler settles. Hosts that
   * supervise a physical child use this so teardown cannot acknowledge before
   * the child's exit event (ADR-0230). Default keeps the prompt-abort contract.
   */
  readonly awaitAbortSettlement?: boolean;
  /**
   * Whether the run's stdout sink is an interactive terminal. Propagated to
   * `ctx.isTTY` for non-redirected segments (a redirected/piped child always
   * gets a non-TTY context). Default `false` — the color-safe default (GNU
   * `--color=auto` emits no SGR when unsure).
   */
  readonly isTTY?: boolean;
  readonly cols?: number;
  readonly rows?: number;
  /** Run-scoped live terminal dimensions for foreground process propagation. */
  readonly terminal?: TerminalResizeSource;
  readonly stdin?: CommandContext['stdin'];
}

type Joiner = '&&' | '||' | ';' | null;
interface Segment {
  readonly tokens: Token[];
  readonly joiner: Joiner; // joiner FOLLOWING this segment; null on the last
}

interface BackgroundJob {
  readonly id: number;
  readonly command: string;
  readonly tokens: readonly Token[];
  readonly controller: AbortController;
  done?: Promise<void>;
  status: ShellJobListItem['status'];
}

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { ignoreBOM: true });
const SIGTERM_EXIT = 143;

function isProcessExit(value: ShellCommandResult): value is ProcessExit {
  return typeof value === 'object' && value !== null;
}

function assertProcessExit(exit: ProcessExit): void {
  const codeExit =
    typeof exit.code === 'number' &&
    Number.isSafeInteger(exit.code) &&
    exit.code >= 0 &&
    exit.signal === null;
  const signalExit = exit.code === null && (exit.signal === 'SIGINT' || exit.signal === 'SIGTERM');
  if (!codeExit && !signalExit) {
    throw new TypeError('process exit must contain exactly one supported code or signal');
  }
}

function assertShellStatus(status: number): void {
  if (!Number.isSafeInteger(status) || status < 0) {
    throw new TypeError(
      `shell command status must be a non-negative safe integer; received ${status}`,
    );
  }
}

/** Numeric shell status for a builtin status or exact supported process exit. */
export function shellCommandExitCode(result: ShellCommandResult): number {
  if (!isProcessExit(result)) {
    assertShellStatus(result);
    return result;
  }
  assertProcessExit(result);
  if (result.code !== null) return result.code;
  return result.signal === 'SIGINT' ? SIGINT_EXIT : SIGTERM_EXIT;
}

function exactExit(result: ShellCommandResult): ProcessExit {
  if (isProcessExit(result)) {
    assertProcessExit(result);
    return result;
  }
  assertShellStatus(result);
  return { code: result, signal: null };
}

function runResult(result: ShellCommandResult, stdout: string, stderr: string): RunResult {
  return {
    exitCode: shellCommandExitCode(result),
    exit: exactExit(result),
    stdout,
    stderr,
  };
}

/**
 * Segment-internal result: `stdoutBytes` is the byte-exact data plane
 * (ADR-0198) feeding pipes and redirects; the public {@link RunResult} keeps
 * only the decoded display-plane string.
 */
interface SegmentResult extends RunResult {
  stdoutBytes: Uint8Array;
}

function concatBytes(chunks: readonly Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

/** Resolved by the abort race when the foreground run is cancelled. */
const ABORTED = Symbol('shell.aborted');

/** Exit code for a command interrupted by SIGINT (128 + SIGINT(2)). */
const SIGINT_EXIT = 130;

/** Package managers that get a directed npm nudge instead of `command not found`. */
const PACKAGE_MANAGERS = new Set(['yarn', 'pnpm', 'bun']);

/** Directed nudge for a recognized package manager (rifty wires only npm). */
function packageManagerNudge(cmd: string): string | null {
  return PACKAGE_MANAGERS.has(cmd)
    ? `${cmd}: not available — rifty wires npm (try: npm install …)\n`
    : null;
}

/**
 * A promise that resolves to {@link ABORTED} when `signal` fires, plus a
 * `cleanup` that detaches the listener so a settled run leaks nothing
 * (ADR-0089 §83). Resolves immediately when already aborted.
 */
function abortRace(signal: AbortSignal): {
  promise: Promise<typeof ABORTED>;
  cleanup: () => void;
} {
  let onAbort: (() => void) | null = null;
  const promise = new Promise<typeof ABORTED>((resolve) => {
    if (signal.aborted) {
      resolve(ABORTED);
      return;
    }
    onAbort = () => resolve(ABORTED);
    signal.addEventListener('abort', onAbort, { once: true });
  });
  return {
    promise,
    cleanup: () => {
      if (onAbort) signal.removeEventListener('abort', onAbort);
    },
  };
}

export class Shell {
  private _cwd: string;
  private readonly env: Record<string, string>;
  private readonly commands: Map<string, ShellCommand> = new Map();
  private readonly customCommands: Map<string, ShellCommand> = new Map();
  private readonly commandResolver: CommandResolver;
  private readonly backgroundJobs: BackgroundJob[] = [];
  private backgroundSeq = 0;
  private readonly execBin?: BinExecutor;
  private readonly fileSystem?: FsSync;
  private readonly mutationGuard?: VfsMutationGuard;
  private readonly assertPortablePaths?: (absolutePaths: readonly string[]) => void;
  private readonly assertCommand?: ShellOptions['assertCommand'];
  private readonly allowBackground: boolean;

  constructor(options: ShellOptions = {}) {
    this._cwd = normalizePath(options.cwd ?? '/');
    this.env = { ...(options.env ?? {}) };
    this.execBin = options.execBin;
    this.fileSystem = options.fileSystem;
    this.mutationGuard = options.mutationGuard;
    this.assertPortablePaths = options.assertPortablePaths;
    this.assertCommand = options.assertCommand;
    this.allowBackground = options.allowBackground ?? true;
    this.commandResolver = new CommandResolver(this.commands, () => this.activeFileSystem());
    const builtins = builtinCommands(
      (p) => {
        this._cwd = p;
      },
      // `which` projects the same live result used by execution and discovery.
      (n) => this.commandResolver.resolve(n, this._cwd),
      () => this.listBackgroundJobs(),
      // `help` is a synopsis of the registered registry, not installed bins.
      () => this.registeredCommandNames(),
    );
    for (const [name, cmd] of Object.entries(builtins)) this.commands.set(name, cmd);
    this.commands.set(
      'npx',
      npx(
        (name, cwd) => this.commandResolver.resolveBin(name, cwd),
        this.execBin,
        this.assertCommand,
      ),
    );
  }

  get cwd(): string {
    return this._cwd;
  }

  private activeFileSystem(): FsSync {
    return this.fileSystem ?? syncMirror();
  }

  envSnapshot(): Record<string, string> {
    return { ...this.env };
  }

  registerCommand(name: string, cmd: ShellCommand): void {
    this.commands.set(name, cmd);
    this.customCommands.set(name, cmd);
  }

  hasCommand(name: string): boolean {
    return this.commandResolver.resolve(name, this._cwd).kind !== 'miss';
  }

  commandNames(): readonly string[] {
    return this.commandResolver.names(this._cwd);
  }

  complete(line: string, cursor: number): ShellCompletionResult | null {
    return createShellCompleter({
      mode: () => 'dev',
      commandNames: () => this.commandResolver.names(this._cwd),
      cwd: () => this._cwd,
      readdirSync: (path) => this.activeFileSystem().readdirSync(path),
    })(line, cursor);
  }

  private registeredCommandNames(): readonly string[] {
    return [...this.commands.keys()].sort();
  }

  async dispose(): Promise<void> {
    for (const job of this.backgroundJobs) {
      if (job.status === 'Running') job.controller.abort();
    }
    await Promise.all(
      this.backgroundJobs
        .map((job) => job.done)
        .filter((done): done is Promise<void> => done !== undefined),
    );
  }

  /**
   * Execute a single shell input line.
   *
   * @param line   the raw line as typed at the terminal
   * @param options per-call hooks (`onChunk` for live streaming — see {@link RunOptions})
   * @returns exit code of the last executed segment plus captured stdout/stderr
   */
  async run(line: string, options: RunOptions = {}): Promise<RunResult> {
    // Inline env overrides (`FOO=bar cmd $FOO`) apply to the command, NOT to
    // expansion on the same line — POSIX: `FOO=bar echo $FOO` prints the OUTER FOO.
    const tokens = tokenize(line, this.env);
    if (tokens.length === 0) return runResult(0, '', '');
    if (!this.allowBackground && tokens.some((token) => isOp(token) && token.op === '&')) {
      throw new NotImplementedError('shell.background', 'background execution is prohibited');
    }

    // A run cancelled before dispatch starts NOTHING — including a trailing
    // background job, which would otherwise fork before the segment pre-check.
    if (options.signal?.aborted) {
      return runResult({ code: null, signal: 'SIGINT' }, '', '');
    }

    const background = this.trailingBackground(line, tokens);
    if (background) {
      if (background.foregroundTokens.length === 0) {
        return this.startBackgroundJob(background.line, background.backgroundTokens, options);
      }
      const foreground = await this.runTokens(background.foregroundTokens, options);
      // `runTokens` owns cancellation only for its foreground prefix. Re-check
      // the host signal before the outer trailing-background dispatcher forks
      // an independent job (`;` / `||` must not turn SIGINT into a fresh run).
      if (options.signal?.aborted) return foreground;
      if (
        (background.joiner === '&&' && foreground.exitCode !== 0) ||
        (background.joiner === '||' && foreground.exitCode === 0)
      ) {
        return foreground;
      }
      const started = await this.startBackgroundJob(
        background.line,
        background.backgroundTokens,
        options,
      );
      return {
        exitCode: started.exitCode,
        exit: started.exit,
        stdout: foreground.stdout + started.stdout,
        stderr: foreground.stderr + started.stderr,
      };
    }

    return this.runTokens(tokens, options);
  }

  private async runTokens(tokens: readonly Token[], options: RunOptions): Promise<RunResult> {
    // Reject non-trailing bare `&` loudly; tokenizer emits it as an operator token.
    if (tokens.some((t) => isOp(t) && t.op === '&')) {
      throw new NotImplementedError(
        'shell.background',
        'only trailing background execution (`cmd &`) is supported',
      );
    }

    const segments = splitOnJoiners(tokens);

    // Per-run cancellation (ADR-0089): the host signal forwards into an
    // internal controller whose `signal` each command observes via `ctx.signal`.
    // A SIGINT resolves `run` (exit 130) even if a handler never returns on its
    // own (a dev server) — cooperative, not a hard kill.
    const controller = new AbortController();
    const host = options.signal;
    const forwardAbort = () => controller.abort();
    if (host) {
      if (host.aborted) controller.abort();
      else host.addEventListener('abort', forwardAbort, { once: true });
    }
    const abort = abortRace(controller.signal);

    let stdout = '';
    let stderr = '';
    let lastExitCode = 0;
    let lastExit: ProcessExit = { code: 0, signal: null };
    let executedAny = false;

    try {
      for (let idx = 0; idx < segments.length; idx++) {
        const seg = segments[idx]!;

        // An empty segment (e.g. the tail of a trailing `;` or `echo a ;`) is a
        // no-op: skip it entirely so it cannot overwrite lastExitCode with 0
        // (`false ;` must stay exit 1).
        if (seg.tokens.length === 0) continue;

        // Short-circuit based on the PREVIOUS segment's joiner.
        if (idx > 0) {
          const prevJoiner = segments[idx - 1]!.joiner;
          if (prevJoiner === '&&' && lastExitCode !== 0) continue;
          if (prevJoiner === '||' && lastExitCode === 0) continue;
          // `;` or null: always run
        }

        // An abort that landed BEFORE this segment (incl. a pre-aborted host
        // signal) cancels it outright — a cancelled command must not start.
        if (controller.signal.aborted) break;

        const segResult = await this.runSegment(
          seg.tokens,
          options,
          controller.signal,
          abort.promise,
        );
        stdout += segResult.stdout;
        stderr += segResult.stderr;
        lastExitCode = segResult.exitCode;
        lastExit = segResult.exit;
        executedAny = true;
        // Ctrl+C aborts the whole line — stop the chain.
        if (controller.signal.aborted) break;
      }
    } finally {
      abort.cleanup();
      if (host) host.removeEventListener('abort', forwardAbort);
    }

    if (!executedAny) {
      // Nothing ran: either every segment was empty (trailing `;` tails — exit
      // 0) or the run was aborted before its first segment — SIGINT's 130,
      // matching a shell's kill-before-start.
      return runResult(controller.signal.aborted ? { code: null, signal: 'SIGINT' } : 0, '', '');
    }

    return { exitCode: lastExitCode, exit: lastExit, stdout, stderr };
  }

  private trailingBackground(
    line: string,
    tokens: readonly Token[],
  ): {
    readonly line: string;
    readonly foregroundTokens: readonly Token[];
    readonly backgroundTokens: readonly Token[];
    readonly joiner: Exclude<Joiner, null> | null;
  } | null {
    const last = tokens[tokens.length - 1];
    if (!last || !isOp(last) || last.op !== '&') return null;
    const body = tokens.slice(0, -1);
    if (body.length === 0) {
      throw new NotImplementedError('shell.background', 'missing command before `&`');
    }
    const joinerIdx = findLastJoinerIndex(body);
    if (joinerIdx === -1) {
      return {
        line: stripTrailingAmp(line),
        foregroundTokens: [],
        backgroundTokens: body,
        joiner: null,
      };
    }
    const joiner = body[joinerIdx];
    if (!joiner || !isOp(joiner) || !isJoiner(joiner.op)) {
      throw new Error('shell.trailingBackground: invalid joiner index');
    }
    const backgroundTokens = body.slice(joinerIdx + 1);
    if (backgroundTokens.length === 0) {
      throw new NotImplementedError('shell.background', 'missing command before `&`');
    }
    return {
      line: tokensToShellLine(backgroundTokens),
      foregroundTokens: body.slice(0, joinerIdx + 1),
      backgroundTokens,
      joiner: joiner.op,
    };
  }

  private async startBackgroundJob(
    line: string,
    tokens: readonly Token[],
    options: RunOptions,
  ): Promise<RunResult> {
    for (const token of tokens) {
      if (!isOp(token)) continue;
      if (token.op === '&') {
        throw new NotImplementedError('shell.background', 'only one trailing `&` is supported');
      }
      if (token.op === '|') {
        throw new NotImplementedError(
          'shell.pipe',
          'pipes in a background job (`a | b &`) are not supported — run the pipeline in the foreground',
        );
      }
      if (token.op === '<') {
        throw new NotImplementedError(
          'shell.input-redirect',
          'input redirect in a background job (`cmd < file &`) is not supported — run it in the foreground',
        );
      }
    }

    const job: BackgroundJob = {
      id: ++this.backgroundSeq,
      command: line,
      tokens,
      controller: new AbortController(),
      status: 'Running',
    };
    this.backgroundJobs.push(job);
    const started = this.formatJob(job);
    options.onChunk?.(started, 'stdout');
    job.done = this.runBackgroundJob(job, options);
    void job.done;
    return runResult(0, started, '');
  }

  private async runBackgroundJob(job: BackgroundJob, options: RunOptions): Promise<void> {
    const shell = this.cloneForBackground();
    try {
      const result = await shell.runTokens(job.tokens, {
        onChunk: options.onChunk,
        signal: job.controller.signal,
        isTTY: options.isTTY,
        cols: options.cols,
        rows: options.rows,
        terminal: options.terminal,
        awaitAbortSettlement: true,
      });
      job.status = result.exitCode === 0 ? 'Done' : `Exit ${result.exitCode}`;
    } catch (err) {
      job.status = 'Exit 1';
      options.onChunk?.(`${job.command}: ${(err as Error).message}\n`, 'stderr');
    } finally {
      options.onChunk?.(this.formatJob(job), 'stdout');
    }
  }

  private cloneForBackground(): Shell {
    const shell = new Shell({
      cwd: this._cwd,
      env: this.env,
      execBin: this.execBin,
      fileSystem: this.fileSystem,
      mutationGuard: this.mutationGuard,
      assertPortablePaths: this.assertPortablePaths,
      assertCommand: this.assertCommand,
      allowBackground: this.allowBackground,
    });
    for (const [name, command] of this.customCommands) shell.registerCommand(name, command);
    return shell;
  }

  private listBackgroundJobs(): readonly ShellJobListItem[] {
    return this.backgroundJobs.map((job) => ({
      id: job.id,
      command: job.command,
      status: job.status,
    }));
  }

  private formatJob(job: ShellJobListItem): string {
    return `[${job.id}] ${job.status} ${job.command}\n`;
  }

  /**
   * Run a single segment (no joiner handling): env-prefix popping,
   * trailing-redirect extraction, command lookup and execution.
   */
  private async runSegment(
    segmentTokens: Token[],
    options: RunOptions,
    signal: AbortSignal,
    abortPromise: Promise<typeof ABORTED>,
  ): Promise<RunResult> {
    if (segmentTokens.length === 0) return runResult(0, '', '');

    // Split into pipeline stages on `|`. One stage is the common path; a
    // multi-stage pipeline BUFFERS each stage's stdout into the next's stdin.
    const stages = splitOnPipe(segmentTokens);
    if (stages.length === 1) {
      return this.runSimpleCommand(stages[0]!, options.stdin, true, options, signal, abortPromise);
    }

    let pipeStdin = options.stdin;
    let stdout = '';
    let stderr = '';
    let exitCode = 0;
    let exit: ProcessExit = { code: 0, signal: null };
    for (let s = 0; s < stages.length; s++) {
      const isLast = s === stages.length - 1;
      // Only the final stage streams stdout to the terminal; every stage's
      // stderr passes through (bash does not pipe stderr). Pipeline exit = the
      // last stage's exit (POSIX; no pipefail).
      const res = await this.runSimpleCommand(
        stages[s]!,
        pipeStdin,
        isLast,
        options,
        signal,
        abortPromise,
      );
      stderr += res.stderr;
      exitCode = res.exitCode;
      exit = res.exit;
      if (signal.aborted) break; // SIGINT cancels the whole pipeline
      if (isLast) stdout = res.stdout;
      // Byte-exact hand-off (ADR-0198): the captured stdout BYTES feed the next
      // stage — re-encoding the display string minted U+FFFD into binary data.
      else pipeStdin = bufferStdin(res.stdoutBytes);
    }
    return { exitCode, exit, stdout, stderr };
  }

  /**
   * Run one simple command (a single pipeline stage): env-prefix popping,
   * `< file` input redirect + trailing `>`/`>>` output redirect extraction,
   * command lookup and execution. `stdin` feeds `ctx.stdin` (a `< file` overrides
   * it); `streamStdout` gates whether stdout chunks reach `onChunk` — false for a
   * non-final pipe stage, whose stdout is captured for the next stage.
   */
  private async runSimpleCommand(
    segmentTokens: Token[],
    stdin: StdinReader | undefined,
    streamStdout: boolean,
    options: RunOptions,
    signal: AbortSignal,
    abortPromise: Promise<typeof ABORTED>,
  ): Promise<SegmentResult> {
    const empty = (): SegmentResult => ({
      exitCode: 0,
      exit: { code: 0, signal: null },
      stdout: '',
      stderr: '',
      stdoutBytes: new Uint8Array(),
    });
    if (segmentTokens.length === 0) return empty();

    // Pop leading KEY=value env assignments (an operator ends the run).
    let i = 0;
    const overrides: Record<string, string> = {};
    while (i < segmentTokens.length) {
      const t = segmentTokens[i]!;
      if (isOp(t)) break;
      const v = t.value;
      const eq = v.indexOf('=');
      if (eq > 0 && /^[A-Za-z_][A-Za-z_0-9]*$/.test(v.slice(0, eq))) {
        overrides[v.slice(0, eq)] = v.slice(eq + 1);
        i++;
      } else break;
    }
    const rest = segmentTokens.slice(i);
    if (rest.length === 0) {
      Object.assign(this.env, overrides);
      return empty();
    }

    // Pull off `> path` / `>> path` redirection. bash removes redirections from
    // ANYWHERE in a simple command, not just the tail — scan right-to-left so the
    // RIGHTMOST target wins (last redirect for the fd) and every `>`/`>>`+word
    // pair leaves the argv (otherwise the op leaks as a literal argument, e.g.
    // `echo hi > out extra`). A target may start with `-` — after `>` it is a
    // filename, never a flag.
    let redirectTo: { path: string; append: boolean } | null = null;
    for (let k = rest.length - 1; k >= 0; k--) {
      const op = rest[k];
      if (!op || !isOp(op) || (op.op !== '>' && op.op !== '>>')) continue;
      const target = rest[k + 1];
      if (!target || isOp(target)) continue; // dangling `>` with no target — leave as-is
      if (!redirectTo) redirectTo = { path: target.value, append: op.op === '>>' };
      rest.splice(k, 2);
    }

    // Symmetric `< path` input redirect: same right-to-left scan, rightmost wins.
    // Reads the file as the stage's stdin (overriding any inherited pipe stdin).
    let redirectFrom: string | null = null;
    for (let k = rest.length - 1; k >= 0; k--) {
      const op = rest[k];
      if (!op || !isOp(op) || op.op !== '<') continue;
      const target = rest[k + 1];
      if (!target || isOp(target)) continue; // dangling `<` with no target — leave as-is
      if (redirectFrom === null) redirectFrom = target.value;
      rest.splice(k, 2);
    }

    const cmdTok = rest[0];
    const cmd = cmdTok && !isOp(cmdTok) ? cmdTok.value : '';
    // Command-name token (rest[0]) stays literal; only ARGUMENTS glob-expand.
    const args = this.expandArgs(rest.slice(1));
    this.assertCommand?.(cmd, args);
    const resolution = this.commandResolver.resolve(cmd, this._cwd);

    const streamDisplayStdout = streamStdout && redirectTo === null;
    // Data plane = BYTES (ADR-0198): stdout captures Uint8Array chunks (strings
    // encode once here); pipes/redirects consume the bytes; the display string
    // decodes from the full buffer at return. stderr stays string-typed — this
    // shell never pipes/redirects stderr (bash parity), it has no data plane.
    const stdoutChunks: Uint8Array[] = [];
    let stderr = '';
    // Streaming decoders for the display plane: byte chunks split mid-multibyte
    // must not mint U+FFFD in onChunk output.
    const stdoutTap = new TextDecoder('utf-8', { ignoreBOM: true });
    const stderrTap = new TextDecoder('utf-8', { ignoreBOM: true });
    const emit = (chunk: string | Uint8Array, stream: ChunkStream): void => {
      // onChunk first so the terminal sees the chunk before it lands in the blob.
      // Redirected stdout and non-final pipe stages capture stdout SILENTLY —
      // they feed a file or the next stage, not the terminal. stderr always
      // streams (bash never pipes stderr).
      // new Uint8Array(view) is a guaranteed COPY — `.slice()` is not: Node
      // Buffer (a Uint8Array subclass real programs write) overrides slice()
      // with an ALIASING view, letting post-write mutation corrupt the
      // captured bytes (review 2026-07-05 handoff r3).
      const bytes = typeof chunk === 'string' ? encoder.encode(chunk) : new Uint8Array(chunk);
      if (stream === 'stdout') {
        const text = stdoutTap.decode(bytes, { stream: true });
        if (streamDisplayStdout) options.onChunk?.(text, 'stdout');
        stdoutChunks.push(bytes);
      } else {
        const text = stderrTap.decode(bytes, { stream: true });
        options.onChunk?.(text, 'stderr');
        stderr += text;
      }
    };
    const result = (
      commandResult: ShellCommandResult,
      exitCodeOverride?: number,
    ): SegmentResult => {
      // Flush the streaming display decoders: a trailing incomplete UTF-8
      // sequence held by a tap must land (as U+FFFD) in onChunk / stderr
      // instead of silently vanishing (review 2026-07-05).
      const stdoutTail = stdoutTap.decode();
      if (stdoutTail && streamDisplayStdout) options.onChunk?.(stdoutTail, 'stdout');
      const stderrTail = stderrTap.decode();
      if (stderrTail) {
        options.onChunk?.(stderrTail, 'stderr');
        stderr += stderrTail;
      }
      const stdoutBytes = concatBytes(stdoutChunks);
      return {
        exitCode: exitCodeOverride ?? shellCommandExitCode(commandResult),
        exit: exactExit(commandResult),
        stdout: decoder.decode(stdoutBytes),
        stderr,
        stdoutBytes,
      };
    };

    // `< file` reads the file as stdin (overrides inherited pipe stdin). A miss
    // is a clean exit-1 diagnostic and the command does NOT run (bash).
    let stageStdin = stdin;
    if (redirectFrom !== null) {
      try {
        const inPath = normalizePath(
          isAbsolute(redirectFrom) ? redirectFrom : joinPath(this._cwd, redirectFrom),
        );
        stageStdin = bufferStdin(this.activeFileSystem().readFileBytesSync(inPath));
      } catch (err) {
        const code = (err as { code?: string }).code;
        const reason = code === 'EISDIR' ? 'Is a directory' : 'No such file or directory';
        emit(`${cmd}: ${redirectFrom}: ${reason}\n`, 'stderr');
        return result(1);
      }
    }

    const isTTY = redirectTo || !streamStdout ? false : (options.isTTY ?? false);
    const terminal = isTTY ? options.terminal : undefined;
    const ctx: CommandContext = {
      cwd: this._cwd,
      env: { ...this.env, ...overrides },
      stdout: {
        write(c: string | Uint8Array): void {
          emit(c, 'stdout');
        },
      },
      stderr: {
        write(c: string | Uint8Array): void {
          emit(c, 'stderr');
        },
      },
      // A redirected OR non-final-pipe sink is never a TTY (ADR-0089): force
      // isTTY false so `ls --color=auto > f` / `ls | cat` write no SGR bytes.
      isTTY,
      ...(redirectTo ? { stdoutIsFile: true } : {}),
      get cols() {
        return terminal?.current().cols ?? options.cols;
      },
      get rows() {
        return terminal?.current().rows ?? options.rows;
      },
      terminal,
      stdin: stageStdin,
      signal,
      fileSystem: this.fileSystem,
      mutationGuard: this.mutationGuard,
      assertPortablePaths: this.assertPortablePaths,
    };

    if (resolution.kind === 'miss') {
      if (resolution.reason === 'missing-path') {
        emit(`${cmd}: No such file or directory\n`, 'stderr');
        return result(127);
      }
      if (resolution.reason === 'not-directory') {
        emit(`${cmd}: Not a directory\n`, 'stderr');
        return result(126);
      }
      if (resolution.reason === 'directory') {
        emit(`${cmd}: is a directory\n`, 'stderr');
        return result(126);
      }
      // A recognized package manager → a directed npm nudge INSTEAD of the
      // generic miss + a wrong `Did you mean 'npm'?`.
      const nudge = packageManagerNudge(cmd);
      if (nudge) {
        emit(nudge, 'stderr');
        return result(127);
      }
      emit(`${cmd}: command not found\n`, 'stderr');
      const suggestion = suggestCommandName(cmd, this.commandResolver.names(this._cwd));
      if (suggestion) emit(`Did you mean '${suggestion}'?\n`, 'stderr');
      return result(127);
    }

    if (resolution.kind === 'file' && !this.execBin) {
      emit(`${cmd}: cannot execute ${resolution.path}: no Node executor configured\n`, 'stderr');
      return result(126);
    }

    // VFS programs share the normal handler path, inheriting SIGINT and
    // redirect behavior. File resolutions always carry a normalized absolute.
    const handler: ShellCommand =
      resolution.kind === 'registered'
        ? resolution.command
        : (a, c) => this.execBin!(resolution.path, a, c);

    let commandResult: ShellCommandResult = 0;
    let exitCodeOverride: number | undefined;
    try {
      const handlerPromise = handler(args, ctx);
      const raced = await Promise.race([handlerPromise, abortPromise]);
      if (raced === ABORTED) {
        if (options.awaitAbortSettlement) {
          const settled = await handlerPromise;
          shellCommandExitCode(settled);
          commandResult = isProcessExit(settled) ? settled : { code: null, signal: 'SIGINT' };
        } else {
          // Prompt-abort hosts detach from a non-cooperative handler; supervise
          // physical children with awaitAbortSettlement instead.
          void handlerPromise.then(undefined, () => {});
          commandResult = { code: null, signal: 'SIGINT' };
        }
        exitCodeOverride = SIGINT_EXIT;
      } else {
        // Validate rich exits while command errors still use the shell's clean
        // diagnostic path rather than leaking a host stack.
        shellCommandExitCode(raced);
        commandResult = raced;
      }
    } catch (err) {
      if (isShellCommandLifecycleFailure(err)) throw err;
      // Clean shell diagnostic — never dump a JS stack trace to the terminal.
      // Commands throw NotImplementedError for unsupported flags/features; its
      // message ("Not implemented: shell.X.flag (…)") is the right altitude.
      emit(`${cmd}: ${(err as Error).message}\n`, 'stderr');
      commandResult = 1;
      exitCodeOverride = undefined;
    }

    // Flush to the redirect target. Truncate/create even when the command wrote
    // nothing — bash opens `> f` before running, so `grep nomatch x > f` empties
    // f. Skip only on a SIGINT abort: don't persist a partial buffer.
    if (redirectTo && (exitCodeOverride ?? shellCommandExitCode(commandResult)) !== SIGINT_EXIT) {
      try {
        const path = normalizePath(
          isAbsolute(redirectTo.path) ? redirectTo.path : joinPath(this._cwd, redirectTo.path),
        );
        const fs = this.activeFileSystem();
        // The captured BYTES flow to the file verbatim (ADR-0198) — encoding
        // the display string minted U+FFFD into binary payloads.
        const payload = concatBytes(stdoutChunks);
        await guardVfsMutations(this.mutationGuard, [{ kind: 'write', path }], () => {
          if (redirectTo.append && fs.existsSync(path)) {
            const existing = fs.readFileBytesSync(path);
            const next = new Uint8Array(existing.length + payload.length);
            next.set(existing, 0);
            next.set(payload, existing.length);
            fs.writeFileSync(path, next);
          } else {
            fs.writeFileSync(path, payload);
          }
        });
        stdoutChunks.length = 0;
      } catch (err) {
        // Loud failure: don't silently dump the redirected payload onto stdout
        // (callers expected a file). Exit 1 + EREDIRECT-tagged stderr so log
        // scanners can detect the failure unambiguously.
        emit(
          `${cmd}: redirect write failed: ${redirectTo.path}: ${(err as Error).message} [EREDIRECT]\n`,
          'stderr',
        );
        stdoutChunks.length = 0;
        commandResult = 1;
      }
    }

    return result(commandResult, exitCodeOverride);
  }

  /**
   * Map argument tokens to argv strings, glob-expanding unquoted word tokens
   * (ADR-0091 part 2). Operators pass through as their literal `op`. A quoted
   * word is NEVER expanded (whole-word quote flag — a documented ADR-0091
   * limitation). An unquoted word with no glob meta passes literally.
   */
  private expandArgs(tokens: Token[]): string[] {
    const out: string[] = [];
    for (const t of tokens) {
      if (isOp(t)) {
        out.push(t.op);
      } else if (t.quoted) {
        out.push(t.value); // quoted word never globs (a quoted '' is KEPT)
      } else if (t.value === '') {
        // An unquoted word that expanded to nothing (`$UNSET`) produces NO word
        // at all (bash word-splitting). Critically stops `rm -rf $UNSET` from
        // collapsing to an empty path that resolves to cwd.
      } else if (!hasGlobMeta(t.value)) {
        out.push(t.value);
      } else {
        out.push(...this.expandGlob(t.value));
      }
    }
    return out;
  }

  /**
   * Single-segment glob expansion of one unquoted word. Splits at the LAST `/`:
   * the prefix resolves the search dir (against `_cwd`), the final segment is
   * the matched pattern. Dotfiles match only when the pattern leads with `.`
   * (bash). Zero matches or a readdir error → the ORIGINAL word verbatim (bash
   * nullglob-off). Multi-segment globbing (glob in a non-final segment) is out
   * of scope. Returns names mapped back through the original prefix, byte-sorted.
   */
  private expandGlob(word: string): string[] {
    const slash = word.lastIndexOf('/');
    const prefix = slash === -1 ? '' : word.slice(0, slash);
    const seg = slash === -1 ? word : word.slice(slash + 1);
    const dir = resolve(this._cwd, prefix === '' ? '.' : prefix);
    const includeDot = seg.startsWith('.');

    let entries: readonly { name: string }[];
    try {
      entries = this.activeFileSystem().readdirSync(dir);
    } catch {
      return [word]; // unreadable dir → literal (nullglob-off)
    }

    const matches: string[] = [];
    for (const entry of entries) {
      if (!includeDot && entry.name.startsWith('.')) continue;
      if (matchSegment(seg, entry.name)) {
        matches.push(prefix === '' ? entry.name : `${prefix}/${entry.name}`);
      }
    }
    if (matches.length === 0) return [word]; // no match → literal
    matches.sort(); // byte (lexicographic) order
    return matches;
  }
}

/**
 * Split tokens on `&&`/`||`/`;` into segments; each carries the joiner that
 * FOLLOWS it (`null` on the last). A trailing joiner (`echo a ;`) yields a final
 * empty segment — the run-loop SKIPS empty segments so they neither run nor
 * reset the exit code.
 */
function splitOnJoiners(tokens: readonly Token[]): Segment[] {
  const segments: Segment[] = [];
  let current: Token[] = [];
  for (const t of tokens) {
    if (isOp(t) && isJoiner(t.op)) {
      segments.push({ tokens: current, joiner: t.op });
      current = [];
    } else {
      current.push(t);
    }
  }
  segments.push({ tokens: current, joiner: null });
  return segments;
}

/** Split a segment's tokens on `|` into pipeline stages (each a `Token[]`). */
function splitOnPipe(tokens: readonly Token[]): Token[][] {
  const stages: Token[][] = [];
  let current: Token[] = [];
  for (const t of tokens) {
    if (isOp(t) && t.op === '|') {
      stages.push(current);
      current = [];
    } else {
      current.push(t);
    }
  }
  stages.push(current);
  return stages;
}

/**
 * One-shot {@link StdinReader} over `bytes`: yields the buffer once, then EOF
 * (`null`); empty input reads EOF immediately. This is the buffered pipe
 * hand-off — a stage's captured stdout becomes the next stage's stdin.
 */
function bufferStdin(bytes: Uint8Array): StdinReader {
  let done = false;
  return {
    read(): Promise<Uint8Array | null> {
      if (done) return Promise.resolve(null);
      done = true;
      return Promise.resolve(bytes.length > 0 ? bytes : null);
    },
  };
}

function isJoiner(op: string): op is Exclude<Joiner, null> {
  return op === '&&' || op === '||' || op === ';';
}

function findLastJoinerIndex(tokens: readonly Token[]): number {
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];
    if (token && isOp(token) && isJoiner(token.op)) return i;
  }
  return -1;
}

function tokensToShellLine(tokens: readonly Token[]): string {
  return tokens.map((token) => (isOp(token) ? token.op : quoteShellWord(token.value))).join(' ');
}

function quoteShellWord(value: string): string {
  if (value.length > 0 && !/[\s'"\\;&|<>]/u.test(value)) return value;
  return `'${value.replace(/'/g, "'\\''")}'`;
}

function stripTrailingAmp(line: string): string {
  let end = line.length;
  while (end > 0 && /\s/u.test(line[end - 1] ?? '')) end--;
  if (line[end - 1] !== '&') return line.trimEnd();
  return line.slice(0, end - 1).trimEnd();
}
